import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { plansAPI } from '../lib/api';
import { useFavourites } from '../context/FavouritesContext';
import { Heart, ArrowRight, HeartBreak } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { logger } from '../lib/logger';

const FavouritesPage = () => {
  const { favourites, toggleFavourite, isFavourite } = useFavourites();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavouritePlans();
  }, [favourites]);

  const loadFavouritePlans = async () => {
    if (favourites.length === 0) {
      setPlans([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // Fetch each favourite plan
      const results = await Promise.all(
        favourites.map(id => plansAPI.getPlan(id).then(r => r.data).catch(() => null))
      );
      setPlans(results.filter(Boolean));
    } catch (err) {
      logger.error('Failed to load favourites', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12" data-testid="favourites-page">
      <div className="container mx-auto px-4">
        <div className="flex items-center space-x-3 mb-8">
          <Heart size={36} weight="fill" className="text-accent" />
          <h1 className="text-4xl font-black text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            My Favourites
          </h1>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-20 bg-white border border-border" data-testid="empty-favourites">
            <HeartBreak size={64} className="text-text_muted mx-auto mb-4" weight="duotone" />
            <p className="text-text_muted mb-2">You haven't favourited any plans yet.</p>
            <p className="text-sm text-text_muted mb-6">Tap the heart on any plan to save it here.</p>
            <Link
              to="/plans"
              className="inline-block bg-primary text-white px-8 py-3 rounded font-medium hover:bg-opacity-90"
              data-testid="browse-plans-link"
            >
              Browse Plans
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.plan_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div className="block bg-white border border-border hover:-translate-y-1 transition-transform group" data-testid={`fav-plan-${plan.plan_id}`}>
                  <Link to={`/plans/${plan.reference_number || plan.plan_id}`} className="block">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={plan.image_url}
                        alt={plan.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavourite(plan.plan_id); }}
                        className="absolute top-3 right-3 p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                        data-testid={`unfav-${plan.plan_id}`}
                        aria-label="Remove from favourites"
                      >
                        <Heart size={18} weight="fill" />
                      </button>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-bold text-primary line-clamp-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                          {plan.name}
                        </h3>
                        <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded ml-2 flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {plan.plan_type}
                        </span>
                      </div>
                      {plan.reference_number && (
                        <div className="text-[10px] font-mono text-text_muted mb-2 uppercase" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          Ref: {plan.reference_number}
                        </div>
                      )}
                      <p className="text-sm text-text_muted mb-4 line-clamp-2">{plan.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          R{plan.base_price.toFixed(2)}
                        </div>
                        <div className="flex items-center text-accent text-sm font-medium group-hover:translate-x-2 transition-transform">
                          <span>View</span>
                          <ArrowRight size={16} weight="bold" className="ml-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavouritesPage;
